/**
 * API Keys Service (desktop / local-only)
 *
 * Thin wrapper around the local FastAPI settings router for managing
 * user-configured API keys stored in the OS keyring.
 *
 * Endpoints used:
 *   POST   /api/v1/settings/api-keys/validate
 *   POST   /api/v1/settings/api-keys/save
 *   DELETE /api/v1/settings/api-keys/{key_name}
 *   GET    /api/v1/settings/api-keys/status
 */

import { api } from './api';

export type ApiKeySource = 'user' | 'environment' | 'none';
export type ApiKeyCategory = 'llm' | 'media' | 'tts' | 'image';

export interface ApiKeyStatusItem {
  key_name: string;
  category: ApiKeyCategory;
  configured: boolean;
  source: ApiKeySource;
}

export interface ApiKeyStatusResponse {
  keyring_available: boolean;
  items: ApiKeyStatusItem[];
}

export interface KeyValidationResult {
  valid: boolean;
  message: string;
  quota_remaining: number | null;
  quota_total: number | null;
}

const BASE_PATH = '/api/v1/settings/api-keys';

export async function getApiKeysStatus(): Promise<ApiKeyStatusResponse> {
  const response = await api.get<ApiKeyStatusResponse>(`${BASE_PATH}/status`);
  return response.data;
}

export async function validateApiKey(
  keyName: string,
  keyValue: string
): Promise<KeyValidationResult> {
  const response = await api.post<KeyValidationResult>(`${BASE_PATH}/validate`, {
    key_name: keyName,
    key_value: keyValue,
  });
  return response.data;
}

export async function saveApiKey(keyName: string, keyValue: string): Promise<void> {
  await api.post(`${BASE_PATH}/save`, {
    key_name: keyName,
    key_value: keyValue,
  });
}

export async function deleteApiKey(keyName: string): Promise<void> {
  await api.delete(`${BASE_PATH}/${encodeURIComponent(keyName)}`);
}
